import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { getReportFooterText } from '../report-utils';

interface ReportLayoutProps {
  title: string;
  description: string;
  isLoading: boolean;
  error: unknown;
  filters: ReactNode;
  children: ReactNode;
}

/**
 * Common layout wrapper for all report pages.
 * Handles loading, error, and no-access states consistently.
 */
export function ReportLayout({
  title,
  description,
  isLoading,
  error,
  filters,
  children,
}: ReportLayoutProps) {
  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state with improved message extraction
  if (error) {
    const getErrorMessage = (err: unknown): string => {
      if (typeof err === 'object' && err !== null) {
        const errorObj = err as any;
        if ('error' in errorObj && typeof errorObj.error === 'string') {
          return errorObj.error;
        }
        if ('data' in errorObj) {
          if (typeof errorObj.data === 'string') {
            return errorObj.data;
          }
          if (typeof errorObj.data === 'object' && errorObj.data !== null) {
            const dataObj = errorObj.data as any;
            if ('message' in dataObj && typeof dataObj.message === 'string') {
              return dataObj.message;
            }
          }
        }
      }
      return 'Failed to load report data. Please try again later.';
    };

    return (
      <Box p={3}>
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      </Box>
    );
  }

  // Success state
  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {description}
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} my={3} flexWrap="wrap" alignItems="flex-start">
        {filters}
      </Box>

      {/* Report content */}
      {children}

      <Divider sx={{ mt: 4 }} />
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        display="block"
        sx={{ mt: 2, pb: 1 }}
      >
        {getReportFooterText()}
      </Typography>
    </Box>
  );
}
